import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, setCsrfToken } from '../services/api';

interface User {
  id: number;
  identifiant: string;
  nom: string;
  prenom: string;
  email: string;
  is_active: boolean;
  login_count: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (emailOrIdentifiant: string, password: string) => Promise<void>;
  logout: () => void;
  register: (data: RegisterData) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

interface RegisterData {
  identifiant: string;
  nom: string;
  prenom: string;
  email: string;
  password: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // SEC-H1 : plus AUCUN élément d'authentification en localStorage.
  // Le JWT est dans un cookie httpOnly (invisible du JS) et le jeton CSRF ne vit
  // qu'en mémoire dans services/api. La seule source de vérité de la session est
  // donc le serveur : au démarrage, on lui demande qui nous sommes.
  const applyAuth = (nextUser: User, csrfToken: string | null) => {
    setCsrfToken(csrfToken);
    setUser(nextUser);
  };

  const clearAuth = () => {
    setCsrfToken(null);
    setUser(null);
  };

  const login = async (emailOrIdentifiant: string, password: string) => {
    try {
      const { data } = await api.post('/api/auth/login', { emailOrIdentifiant, password });
      if (data.success && data.user) {
        applyAuth(data.user, data.csrfToken ?? null);
      } else {
        throw new Error('Réponse invalide du serveur');
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Erreur lors de la connexion';
      console.error('Erreur de connexion:', msg);
      throw new Error(msg);
    }
  };

  const register = async (registerData: RegisterData) => {
    try {
      const { data } = await api.post('/api/auth/register', registerData);
      if (data.success && data.user) {
        applyAuth(data.user, data.csrfToken ?? null);
      } else {
        throw new Error('Réponse invalide du serveur');
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Erreur lors de l\'inscription';
      console.error('Erreur d\'inscription:', msg);
      throw new Error(msg);
    }
  };

  // Le serveur réémet le JWT (et donc un NOUVEAU jeton CSRF) pour que la session
  // courante survive au changement. Sans reprendre ce jeton, la requête mutante
  // suivante échouerait en 403 INVALID_CSRF et déconnecterait l'utilisateur.
  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const { data } = await api.put('/api/auth/password', { currentPassword, newPassword });
      if (!data.success) {
        throw new Error(data.error || 'Réponse invalide du serveur');
      }
      setCsrfToken(data.csrfToken ?? null);
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Erreur lors du changement de mot de passe';
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      // Indispensable : le cookie étant httpOnly, seul le serveur peut l'effacer.
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      clearAuth();
    }
  };

  // Restauration de session au chargement : le cookie part tout seul avec la requête.
  // Un seul appel, au montage — le login n'en déclenche pas un second (BUG-M4),
  // puisqu'il reçoit déjà l'utilisateur et le jeton CSRF dans sa réponse.
  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      try {
        const { data } = await api.get('/api/auth/me');
        if (cancelled) return;
        if (data.success && data.user) {
          applyAuth(data.user, data.csrfToken ?? null);
        } else {
          clearAuth();
        }
      } catch (error) {
        // 401 attendu quand il n'y a pas (ou plus) de cookie valide : simple visiteur.
        if (!cancelled) clearAuth();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // BUG-H10 : déconnexion douce déclenchée par l'intercepteur axios (401 / CSRF invalide),
  // sans reload de page.
  useEffect(() => {
    const handleAuthLogout = () => clearAuth();
    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    register,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};